import { type TimestampFormat } from "@t3tools/contracts/settings";

import { i18n } from "./i18n";
import { t } from "~/i18n";

export function getTimestampFormatOptions(
  timestampFormat: TimestampFormat,
  includeSeconds: boolean,
): Intl.DateTimeFormatOptions {
  const baseOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    ...(includeSeconds ? { second: "2-digit" } : {}),
  };

  if (timestampFormat === "locale") {
    return baseOptions;
  }

  return {
    ...baseOptions,
    hour12: timestampFormat === "12-hour",
  };
}

const timestampFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getTimestampFormatter(
  timestampFormat: TimestampFormat,
  includeSeconds: boolean,
): Intl.DateTimeFormat {
  const cacheKey = `${i18n.language}:${timestampFormat}:${includeSeconds ? "seconds" : "minutes"}`;
  const cachedFormatter = timestampFormatterCache.get(cacheKey);
  if (cachedFormatter) {
    return cachedFormatter;
  }

  const formatter = new Intl.DateTimeFormat(
    i18n.language || undefined,
    getTimestampFormatOptions(timestampFormat, includeSeconds),
  );
  timestampFormatterCache.set(cacheKey, formatter);
  return formatter;
}

export function formatTimestamp(isoDate: string, timestampFormat: TimestampFormat): string {
  return getTimestampFormatter(timestampFormat, true).format(new Date(isoDate));
}

export function formatShortTimestamp(isoDate: string, timestampFormat: TimestampFormat): string {
  return getTimestampFormatter(timestampFormat, false).format(new Date(isoDate));
}

/**
 * Format a relative time string from an ISO date.
 * Returns `{ value: "20s", suffix: "ago" }` or `{ value: "just now", suffix: null }`
 * so callers can style the numeric portion independently.
 */
export function formatRelativeTime(isoDate: string): { value: string; suffix: string | null } {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  if (diffMs < 0) return { value: t("common/justNow_bpan51"), suffix: null };
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return { value: t("common/justNow_bpan51"), suffix: null };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
    return { value: t("common/m_dv7blw", { minutes }), suffix: t("common/ago_bb0k83") };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { value: t("common/h_lrzvx9", { hours }), suffix: t("common/ago_bb0k83") };
  const days = Math.floor(hours / 24);
  return { value: t("common/d_muo1nl", { days }), suffix: t("common/ago_bb0k83") };
}

export function formatRelativeTimeLabel(isoDate: string) {
  const relative = formatRelativeTime(isoDate);
  return relative.suffix ? `${relative.value} ${relative.suffix}` : relative.value;
}

/**
 * Relative elapsed duration since an ISO instant, without an "ago" suffix.
 * Useful for labels like "Connected for 3m".
 */
export function formatElapsedDurationLabel(isoDate: string, nowMs: number = Date.now()): string {
  const diffMs = nowMs - new Date(isoDate).getTime();
  if (diffMs <= 0) return t("common/justNow_bpan51");

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 5) return t("common/justNow_bpan51");
  if (seconds < 60) return t("common/s_tqgrlz", { seconds });

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t("common/m_dv7blw", { minutes });

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("common/h_lrzvx9", { hours });

  const days = Math.floor(hours / 24);
  return t("common/d_muo1nl", { days });
}

/**
 * Relative time until an ISO instant (e.g. expiry). Mirrors {@link formatRelativeTime} but for future times.
 */
export function formatRelativeTimeUntil(isoDate: string): { value: string; suffix: string | null } {
  const diffMs = new Date(isoDate).getTime() - Date.now();
  if (diffMs <= 0) return { value: t("common/expired_sm1zv7"), suffix: null };
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 5) return { value: t("common/soon_jj0zui"), suffix: null };
  if (seconds < 60)
    return { value: t("common/s_tqgrlz", { seconds }), suffix: t("common/left_nz2b15") };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
    return { value: t("common/m_dv7blw", { minutes }), suffix: t("common/left_nz2b15") };
  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return { value: t("common/h_lrzvx9", { hours }), suffix: t("common/left_nz2b15") };
  const days = Math.floor(hours / 24);
  return { value: t("common/d_muo1nl", { days }), suffix: t("common/left_nz2b15") };
}

/**
 * Countdown for a future instant (e.g. link expiry): "Expires in 4m 12s", with second precision under one hour.
 * Pass `nowMs` when a parent tick drives re-renders so the diff matches that snapshot.
 */
export function formatExpiresInLabel(isoDate: string, nowMs: number = Date.now()): string {
  const diffMs = new Date(isoDate).getTime() - nowMs;
  if (diffMs <= 0) return t("common/expired_sm1zv7");

  const totalSeconds = Math.floor(diffMs / 1000);
  if (totalSeconds < 5) return t("common/expiresInAMoment_6b3ty3");
  if (totalSeconds < 60)
    return t("common/expiresIn_tb42a9", { "0": formatDuration(0, 0, 0, totalSeconds) });

  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return t("common/expiresIn_tb42a9", { "0": formatDuration(0, 0, minutes, seconds) });
  }

  if (totalSeconds < 86_400) {
    const hours = Math.floor(totalSeconds / 3600);
    const rem = totalSeconds % 3600;
    return t("common/expiresIn_tb42a9", {
      "0": formatDuration(0, hours, Math.floor(rem / 60), rem % 60),
    });
  }

  const days = Math.floor(totalSeconds / 86_400);
  const remAfterDays = totalSeconds % 86_400;
  const hours = Math.floor(remAfterDays / 3600);
  const rem = remAfterDays % 3600;
  return t("common/expiresIn_tb42a9", {
    "0": formatDuration(days, hours, Math.floor(rem / 60), rem % 60),
  });
}

function formatDuration(days: number, hours: number, minutes: number, seconds: number): string {
  const parts: string[] = [];
  if (days > 0) parts.push(t("common/d_muo1nl", { days }));
  if (hours > 0) parts.push(t("common/h_lrzvx9", { hours }));
  if (minutes > 0) parts.push(t("common/m_dv7blw", { minutes }));
  if (seconds > 0) parts.push(t("common/s_tqgrlz", { seconds }));
  if (parts.length === 0) parts.push(t("common/s_tqgrlz", { seconds }));
  return parts.join(" ");
}
